import type { Agent, Project, Provider, ProviderListResponse } from "@openanalyst-ai/sdk/v2/client"
import { NormalizedProviderListResponse } from "@openanalyst-ai/ui/context"
export { pathKey as directoryKey, type PathKey as DirectoryKey } from "@/utils/path-key"

export const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// The ONE ordering for a session's message list: creation time, then id as a
// same-millisecond tie-breaker — the same rule the engine's `latest()` uses. Message
// ids are NOT reliable order on their own: their sortable prefix wraps (it did on
// 2026-08-14), after which every new message id sorts below every older one, and an
// id-ordered list rendered the newest message at the TOP of the thread. Every sort
// and every insertion into a message array must go through this; lookups by id alone
// cannot binary-search a time-ordered array and must scan instead.
export const compareMessages = (a: { id: string; time: { created: number } }, b: { id: string; time: { created: number } }) =>
  a.time.created - b.time.created || cmp(a.id, b.id)

/** Index at which `message` belongs in a `compareMessages`-ordered array (first position with a greater element). */
export function messageInsertIndex<T extends { id: string; time: { created: number } }>(messages: readonly T[], message: T) {
  let left = 0
  let right = messages.length
  while (left < right) {
    const mid = (left + right) >>> 1
    if (compareMessages(messages[mid]!, message) <= 0) left = mid + 1
    else right = mid
  }
  return left
}

function isAgent(input: unknown): input is Agent {
  if (!input || typeof input !== "object") return false
  const item = input as { name?: unknown; mode?: unknown }
  if (typeof item.name !== "string") return false
  return item.mode === "subagent" || item.mode === "primary" || item.mode === "all"
}

export function normalizeAgentList(input: unknown): Agent[] {
  if (Array.isArray(input)) return input.filter(isAgent)
  if (isAgent(input)) return [input]
  if (!input || typeof input !== "object") return []
  return Object.values(input).filter(isAgent)
}

export function normalizeProviderList(input: ProviderListResponse): NormalizedProviderListResponse {
  const all = input.all as Provider[] | Map<string, Provider>
  const providers = Array.isArray(all) ? all : [...all.values()]
  return {
    ...input,
    all: providers.map((provider) => ({
      ...provider,
      models: Object.fromEntries(
        Object.entries(provider.models).filter(([, info]) => (info as { status?: string }).status !== "deprecated"),
      ),
    })),
  }
}

export function sanitizeProject(project: Project) {
  if (!project.icon?.url && !project.icon?.override) return project
  return {
    ...project,
    icon: {
      ...project.icon,
      url: undefined,
      override: undefined,
    },
  }
}
